import { environment } from 'environments/environment';

export interface SidebarPlatform {
  id: string;
  routePath: string;
  displayName: string;
}

export interface MenuItem {
  name: string;
  link: string;
  fullPath?: string;
  groupPath?: string[];
  expanded?: boolean;
  children?: MenuItem[];
  platformId?: string;
}

export function buildMenuItems(platforms: SidebarPlatform[]): MenuItem[] {
  return [
    {
      name: 'Trading',
      link: 'trading',
      children: [
        {
          name: 'Trade',
          link: 'trade',
        },
        {
          name: 'Limit orders',
          link: 'limit-orders',
        },
        {
          name: 'TWAP orders',
          link: 'twap-orders',
        },
        {
          name: 'Synthetic Trading',
          link: 'synthetic-trading',
        },
        {
          name: 'Orders',
          link: 'orders',
        },
        {
          name: 'Crypto.com',
          link: 'crypto-com',
        },
      ],
    },
    {
      name: 'Analytics',
      link: 'analytics',
      children: [
        {
          name: 'Aggregated',
          link: 'aggregated',
        },
        {
          name: 'Order book',
          link: 'order-book',
        },
        {
          name: 'LP quality stats',
          link: 'lp-quality-stats',
        },
        {
          name: 'Reports',
          link: 'reports',
          children: [
            {
              name: 'Book report',
              link: 'book-report',
            },
            ...(environment.isBermuda || !environment.production
              ? [
                  {
                    name: 'Top of Book History',
                    link: 'top-of-book-history',
                  },
                  {
                    name: 'TSOE FIX report',
                    link: 'tsoe-fix-report',
                  },
                  {
                    name: 'Spot Position History',
                    link: 'spot-position-history',
                  },
                  {
                    name: 'Perp Position History',
                    link: 'perp-position-history',
                  },
                ]
              : []),
          ],
        },
      ],
    },
    {
      name: 'Accounts',
      link: 'accounts',
      children: [
        {
          name: 'Details',
          link: 'details',
        },
        {
          name: 'Obligations',
          link: 'obligations',
        },
        {
          name: 'Banks',
          link: 'banks',
        },
      ],
    },
    {
      name: 'Reports',
      link: 'reports',
      children: [
        {
          name: 'Admin report',
          link: 'admin-report',
        },
        {
          name: 'Balance report',
          link: 'balance-report',
        },
        {
          name: 'Client report',
          link: 'client-report',
        },
        {
          name: 'LP orders report',
          link: 'lp-orders-report',
        },
        {
          name: 'RFQ report',
          link: 'rfq-report',
        },
        ...(!environment.isBermuda
          ? [
              {
                name: 'Referral agent report',
                link: 'referral-agent',
              },
            ]
          : []),
      ],
    },
    {
      name: 'Transfers',
      link: 'transfers',
      children: [
        {
          name: 'Pending',
          link: 'pending',
        },
        {
          name: 'Withdrawals',
          link: 'withdrawals',
        },
        {
          name: 'Audit',
          link: 'audit',
        },
        {
          name: 'History',
          link: 'history',
        },
        ...(!environment.isBermuda
          ? [
              {
                name: 'Luminus Import',
                link: 'luminus-import',
              },
            ]
          : []),
      ],
    },
    ...(!environment.isBermuda
      ? [
          {
            name: 'Venue transfers',
            link: 'venue-transfers',
            children: [
              {
                name: 'Transfers',
                link: 'transfers',
              },
              {
                name: 'Routes',
                link: 'routes',
              },
            ],
          },
        ]
      : []),
    {
      name: 'Admins',
      link: 'users',
    },
    {
      name: 'Settings',
      link: 'settings',
      children: [
        {
          name: 'Liquidity providers',
          link: 'liquidity-providers',
        },
        {
          name: 'Assets',
          link: 'assets',
        },
        {
          name: 'Pairs',
          link: 'pairs',
        },
        ...(!environment.isBermuda
          ? [
              {
                name: 'Referral agents',
                link: 'referral-agents',
              },
            ]
          : []),
        {
          name: 'Deposit instructions',
          link: 'deposit-instructions',
        },
        {
          name: 'FIX logging',
          link: 'fix-logging',
        },
        {
          name: 'Risk exposure alerts',
          link: 'risk-exposure-alerts',
        },
        ...(environment.isBermuda || !environment.production
          ? [
              {
                name: 'Hidden road',
                link: 'hidden-road',
              },
            ]
          : []),
        {
          name: 'Permanent sessions',
          link: 'permanent-sessions',
        },
        {
          name: 'Top of book',
          link: 'top-of-book',
        },
        {
          name: 'Travel rule',
          link: 'travel-rule',
        },
        {
          name: 'MM platforms',
          link: 'market-making-platforms',
        },
        {
          name: 'Reporting codes',
          link: 'reporting-codes',
        },
        {
          name: 'Route presets',
          link: 'route-presets',
        },
        {
          name: 'Maintenance',
          link: 'maintenance',
        },
      ],
    },
    {
      name: 'Fireblocks',
      link: 'fireblocks',
      children: [
        {
          name: 'Assets',
          link: 'assets',
        },
        {
          name: 'Balances',
          link: 'balances',
        },
        {
          name: 'Vaults',
          link: 'vaults',
        },
        {
          name: 'External wallets',
          link: 'external-wallets',
        },
        {
          name: 'Network connections',
          link: 'network-connections',
        },
        {
          name: 'Settings',
          link: 'settings',
        },
      ],
    },
    {
      name: 'Internal LPs',
      link: 'internal-lps',
      children: [
        {
          name: 'House LP',
          link: 'house-lp',
          children: [
            {
              name: 'Dashboard',
              link: 'dashboard',
            },
            {
              name: 'Trades',
              link: 'trades',
            },
          ],
        },
        ...(!environment.isBermuda
          ? [
              {
                name: 'B book LP',
                link: 'b-book-lp',
                children: [
                  {
                    name: 'Settings',
                    link: 'settings',
                  },
                  {
                    name: 'Logs',
                    link: 'logs',
                  },
                  {
                    name: 'Trading',
                    link: 'trading',
                  },
                ],
              },
            ]
          : []),
        ...(environment.isBermuda || !environment.production
          ? [
              {
                name: 'Main book LP',
                link: 'main-book-lp',
                children: [
                  {
                    name: 'Pairs settings',
                    link: 'pairs-settings',
                  },
                  {
                    name: 'Hedge settings',
                    link: 'hedge-settings',
                  },
                  {
                    name: 'Risk',
                    link: 'risk',
                  },
                  {
                    name: 'Trading',
                    link: 'trading',
                  },
                  {
                    name: 'Trades',
                    link: 'trades',
                  },
                ],
              },
            ]
          : []),
        {
          name: 'Principal risk LP',
          link: 'principal-risk-lp',
          children: [
            {
              name: 'Settings',
              link: 'settings',
            },
            {
              name: 'Status',
              link: 'status',
            },
            {
              name: 'Logs',
              link: 'logs',
            },
          ],
        },
        {
          name: 'Synthetic LP',
          link: 'synthetic-lp',
          children: [
            {
              name: 'Settings',
              link: 'settings',
            },
          ],
        },
      ],
    },
    {
      name: 'Logs',
      link: 'logs',
      children: [
        {
          name: 'User sessions',
          link: 'user-sessions',
        },
        {
          name: 'Transaction log',
          link: 'transaction-log',
        },
        {
          name: 'System audit log',
          link: 'system-log',
        },
        {
          name: 'Downloads log',
          link: 'downloads-log',
        },
        {
          name: 'Performance log',
          link: 'performance-log',
        },
        {
          name: 'Memory log',
          link: 'memory-log',
        },
      ],
    },
    {
      name: 'Accounting',
      link: 'accounting',
      children: [
        {
          name: 'Fees',
          link: 'fees',
        },
        {
          name: 'Balances',
          link: 'balances',
        },
        {
          name: '(Un)realized P&L',
          link: 'pnl',
        },
      ],
    },
    {
      name: 'Leaderboard',
      link: 'leaderboard',
    },

    {
      name: 'Market Making',
      link: 'market-making',
      children: [
        ...platforms.map(platform => ({
          name: platform.displayName,
          link: platform.routePath,
          children: [
            {
              name: 'Dashboard',
              link: 'dashboard',
            },
            {
              name: 'External trades',
              link: 'external-trades',
            },
            {
              name: 'Balances',
              link: 'balances',
              platformId: platform.id,
            },
            {
              name: 'Second leg',
              link: 'second-leg',
            },
            {
              name: 'Settings',
              link: 'settings',
            },
          ],
        })),
        {
          name: 'Finery markets',
          link: 'finery-markets',
        },
        {
          name: 'Investigate Trades',
          link: 'investigator',
        },
      ],
    },
  ].map(menuItem => ({
    ...menuItem,
    name: menuItem.name.toUpperCase(),
  }));
}
